import { useEffect, useState } from 'react';
import {
  useTheme,
  Box,
  Typography,
  Switch,
  Tooltip,
  alpha,
} from '@mui/material';
import { tokens } from '../../global/theme/tokens';
import ReactAce from 'react-ace';
import ace from 'ace-builds';
import 'ace-builds/src-noconflict/mode-html';
import 'ace-builds/src-noconflict/theme-monokai';
import { HTMLHint } from 'htmlhint';
import { html as beautifyHtml } from 'js-beautify';
import { useTranslation } from 'react-i18next';
import HighlightIcon from '@mui/icons-material/Highlight';
import PreviewIcon from '@mui/icons-material/Preview';
import KeyboardIcon from '@mui/icons-material/Keyboard';

// Disable Ace worker to prevent "Load failed" errors in Safari
ace.config.set('useWorker', false);

const CodeEditor = ({
  data,
  setData,
  editorRef = null,
  height = 800,
  className = '',
  variables = [],
  currentKey,
  metaData,
  disableTemplateRendering,
  changeDisableTemplateRendering,
  templateBlocks,
  onUnsavedStatusChange,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const [code, setCode] = useState(data);

  useEffect(() => {
    setCode(data);
  }, [currentKey]);

  // Report unsaved status to parent
  useEffect(() => {
    if (onUnsavedStatusChange) {
      onUnsavedStatusChange(data !== code);
    }
  }, [data, code, onUnsavedStatusChange]);

  const handleCodeChange = (newCode) => {
    setCode(newCode);
    const editor = editorRef.current.editor;
    const annotations = lintHtml(newCode);
    editor.getSession().setAnnotations(annotations);
  };

  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
      undo();
    }
    if ((e.ctrlKey || e.metaKey) && e.key === 'y') {
      redo();
    }
  };

  function undo() {
    editorRef.current.editor.undo();
  }

  function redo() {
    editorRef.current.editor.redo();
  }

  useEffect(() => {
    const editor = editorRef.current.editor;
    if (!editor) {
      return;
    }

    editor.container.addEventListener('keydown', handleKeyDown);

    return () => {
      editor.container.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const htmlHintConfig = {
    'doctype-first': false, // Disable "Doctype must be declared"
    'tag-pair': true, // Example: still check for unclosed tags
    'id-unique': true, // Example: still check for duplicate IDs
    // ...you can add more rules if needed
  };
  function lintHtml(code) {
    const results = HTMLHint.verify(code, htmlHintConfig);
    return results.map((err) => ({
      row: err.line - 1,
      column: err.col - 1,
      text: err.message,
      type: 'error',
    }));
  }
  // Register Cmd+S / Ctrl+S
  useEffect(() => {
    const editor = editorRef.current.editor;

    const handleFormatCommand = (event) => {
      // Prevent browser save
      if (event) event.preventDefault();

      const code = editor.getValue();
      const formatted = beautifyHtml(code, {
        indent_size: 2,
        wrap_line_length: 80,
      });
      setData(formatted);
      editor.setValue(formatted, 1);
    };

    editor.commands.addCommand({
      name: 'formatOnSave',
      bindKey: { win: 'Ctrl-S', mac: 'Command-S' },
      exec: () => handleFormatCommand(),
      readOnly: false,
    });

    // 🔥 Extra safety: globally stop browser save shortcut
    const preventSave = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        handleFormatCommand();
      }
    };

    window.addEventListener('keydown', preventSave);
    return () => window.removeEventListener('keydown', preventSave);
  }, [currentKey, setData]);

  // variable highlighting
  const [highlightVariables, setHighlightVariables] = useState(true);
  useEffect(() => {
    if (!highlightVariables) {
      // cleanup if disabled
      const contentLayer =
        editorRef.current.editor.renderer.container.querySelector(
          '.ace_content'
        );
      contentLayer?.querySelectorAll('.var-chip').forEach((el) => el.remove());
      return;
    }

    const editor = editorRef.current.editor;
    const session = editor.getSession();
    const renderer = editor.renderer;

    editor.container.style.fontFamily = 'monospace';

    const pattern = new RegExp(
      variables
        .filter((v) => v.value)
        .map((v) => v.value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
        .join('|') || '(?!)', // (?!) is a regex that never matches
      'g'
    );

    const patternBlocks = new RegExp(
      (templateBlocks?.data || [])
        .filter((v) => v.key)
        .map((v) => v.key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
        .join('|') || '(?!)',
      'g'
    );

    const contentLayer = renderer.container.querySelector('.ace_content');

    const renderVariableChips = () => {
      contentLayer.querySelectorAll('.var-chip').forEach((el) => el.remove());

      const config = renderer.layerConfig;
      const firstRow = renderer.getFirstVisibleRow();
      const lastRow = renderer.getLastVisibleRow();

      for (let row = firstRow; row <= lastRow; row++) {
        const line = session.getLine(row);
        if (!line) continue;

        let match;
        while ((match = pattern.exec(line)) !== null) {
          const variable = variables.find((v) => v.value === match[0]);
          if (!variable) continue;

          const startCol = match.index;
          const endCol = startCol + match[0].length;

          const start = session.documentToScreenPosition(row, startCol);
          const end = session.documentToScreenPosition(row, endCol);

          const top = renderer.$cursorLayer.getPixelPosition(
            { row: start.row, column: 0 },
            true
          ).top;
          const left = start.column * renderer.characterWidth;
          const width = (end.column - start.column) * renderer.characterWidth;
          const height = renderer.lineHeight - 2;

          const chip = document.createElement('div');
          chip.className = 'var-chip';
          chip.textContent = variable.name;
          chip.style.position = 'absolute';
          chip.style.left = `${left + 4}px`;
          chip.style.top = `${top - 1}px`;
          chip.style.width = `${width}px`;
          chip.style.height = `${height + 4}px`;
          chip.style.zIndex = 10;
          chip.style.padding = '2px 0px';
          chip.style.textAlign = 'center';
          chip.style.backgroundColor = colors.info;
          contentLayer.appendChild(chip);
        }
        if (templateBlocks && templateBlocks?.data.length > 0)
          while ((match = patternBlocks.exec(line)) !== null) {
            const block = templateBlocks?.data.find((v) => v.key === match[0]);

            if (!block) continue;

            const startCol = match.index;
            const endCol = startCol + match[0].length;

            const start = session.documentToScreenPosition(row, startCol);
            const end = session.documentToScreenPosition(row, endCol);

            const top = renderer.$cursorLayer.getPixelPosition(
              { row: start.row, column: 0 },
              true
            ).top;
            const left = start.column * renderer.characterWidth;
            const width = (end.column - start.column) * renderer.characterWidth;
            const height = renderer.lineHeight - 2;

            const chip = document.createElement('div');
            chip.className = 'var-chip';
            chip.textContent = block.label;
            chip.style.position = 'absolute';
            chip.style.left = `${left + 4}px`;
            chip.style.top = `${top - 1}px`;
            chip.style.width = `${width}px`;
            chip.style.height = `${height + 4}px`;
            chip.style.zIndex = 10;
            chip.style.padding = '2px 0px';
            chip.style.textAlign = 'center';
            chip.style.backgroundColor = colors.warning;
            contentLayer.appendChild(chip);
          }
      }
    };

    const rerender = () => renderVariableChips();

    editor.on('change', rerender);
    renderer.on('afterRender', rerender);
    session.on('changeScrollTop', rerender);
    session.on('changeScrollLeft', rerender);

    renderVariableChips();

    return () => {
      editor.off('change', rerender);
      renderer.off('afterRender', rerender);
      session.off('changeScrollTop', rerender);
      session.off('changeScrollLeft', rerender);

      contentLayer.querySelectorAll('.var-chip').forEach((el) => el.remove());
    };
  }, [highlightVariables, templateBlocks]);

  return (
    <Box
      className="w-full h-full flex flex-col relative"
      sx={{
        '.var-chip': {
          position: 'absolut',
          color: '#000',
          fontSize: '10px',
          padding: '0 4px',
          borderRadius: '3px',
          pointerEvents: 'none',
          fontFamily: 'monospace',
          whiteSpace: 'nowrap',
        },
      }}
      onKeyDown={(e) => e.stopPropagation()}
    >
      {/* Modern Toolbar */}
      <Box
        className="flex items-center justify-between px-4 py-2"
        sx={{
          backgroundColor: colors.glass,
          backdropFilter: 'blur(8px)',
          borderBottom: `1px solid ${alpha(colors.card, 0.2)}`,
        }}
      >
        {/* Left: Save status indicator */}
        <Box className="flex items-center gap-3">
          <Box
            className="flex items-center gap-2"
            sx={{
              px: 1.5,
              py: 0.5,
              borderRadius: '8px',
              backgroundColor:
                data === code
                  ? alpha(colors.success, 0.1)
                  : alpha(colors.warning, 0.1),
              transition: 'all 0.3s ease',
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor:
                  data === code ? colors.success : colors.warning,
                boxShadow:
                  data === code
                    ? `0 0 8px ${colors.success}`
                    : `0 0 8px ${colors.warning}`,
                animation: data === code ? 'none' : 'pulse 2s infinite',
                '@keyframes pulse': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.5 },
                },
              }}
            />
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 500,
                color: data === code ? colors.success : colors.warning,
              }}
            >
              {data === code ? t('Synced') : t('Editing')}
            </Typography>
          </Box>

          {data !== code && (
            <Box
              className="flex items-center gap-1"
              sx={{
                opacity: 0.5,
                fontSize: '11px',
              }}
            >
              <KeyboardIcon sx={{ fontSize: 14 }} />
              <span>⌘S {t('to sync')}</span>
            </Box>
          )}
        </Box>

        {/* Right: Toggle controls - Modern switches */}
        <Box className="flex items-center gap-4">
          <Tooltip
            title={t('Highlight template variables in the editor')}
            arrow
          >
            <Box
              className="flex items-center gap-2 cursor-pointer"
              onClick={() => setHighlightVariables(!highlightVariables)}
              sx={{
                px: 1.5,
                py: 0.5,
                borderRadius: '8px',
                backgroundColor: highlightVariables
                  ? alpha(colors.info, 0.15)
                  : 'transparent',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: alpha(colors.info, 0.1),
                },
              }}
            >
              <HighlightIcon
                sx={{
                  fontSize: 16,
                  color: highlightVariables ? colors.info : 'text.secondary',
                  transition: 'color 0.2s ease',
                }}
              />
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: highlightVariables ? 500 : 400,
                  color: highlightVariables ? colors.info : 'text.secondary',
                }}
              >
                {t('Highlight')}
              </Typography>
              <Switch
                size="small"
                checked={highlightVariables}
                onChange={() => setHighlightVariables(!highlightVariables)}
                sx={{
                  ml: 0.5,
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: colors.info,
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: colors.info,
                  },
                }}
              />
            </Box>
          </Tooltip>

          <Tooltip title={t('Show variable values in preview')} arrow>
            <Box
              className="flex items-center gap-2 cursor-pointer"
              onClick={() =>
                changeDisableTemplateRendering(!disableTemplateRendering)
              }
              sx={{
                px: 1.5,
                py: 0.5,
                borderRadius: '8px',
                backgroundColor: !disableTemplateRendering
                  ? alpha(colors.success, 0.15)
                  : 'transparent',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: alpha(colors.success, 0.1),
                },
              }}
            >
              <PreviewIcon
                sx={{
                  fontSize: 16,
                  color: !disableTemplateRendering
                    ? colors.success
                    : 'text.secondary',
                  transition: 'color 0.2s ease',
                }}
              />
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: !disableTemplateRendering ? 500 : 400,
                  color: !disableTemplateRendering
                    ? colors.success
                    : 'text.secondary',
                }}
              >
                {t('Preview')}
              </Typography>
              <Switch
                size="small"
                checked={!disableTemplateRendering}
                onChange={() =>
                  changeDisableTemplateRendering(!disableTemplateRendering)
                }
                sx={{
                  ml: 0.5,
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: colors.success,
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: colors.success,
                  },
                }}
              />
            </Box>
          </Tooltip>
        </Box>
      </Box>

      {/* Code Editor */}
      <ReactAce
        mode="html"
        theme="monokai"
        onChange={handleCodeChange}
        value={code}
        highlightActiveLine={true}
        ref={editorRef}
        setOptions={{
          enableBasicAutocompletion: true,
          enableLiveAutocompletion: true,
          enableSnippets: true,
          showLineNumbers: true,
        }}
        style={{
          width: '100%',
          height: '100%',
          borderRadius: '0 0 12px 12px',
        }}
        className="h-full flex-1"
      />
    </Box>
  );
};

export default CodeEditor;
